/**
 * Console Logger - Forwards JavaScript console logs to Android Logcat
 *
 * This utility intercepts console methods and forwards them to native Android logs
 * so they appear in Android Studio's Logcat with the tag "JSConsole"
 */

import { Capacitor } from '@capacitor/core';

class ConsoleLogger {
  constructor() {
    this.isNative = Capacitor.isNativePlatform();
    this.isAndroid = Capacitor.getPlatform() === 'android';
    this.originalConsole = {
      log: console.log.bind(console),
      warn: console.warn.bind(console),
      error: console.error.bind(console),
      info: console.info.bind(console),
      debug: console.debug.bind(console),
    };
  }

  /**
   * Forwards a message to Android's native log system
   * @param {string} method - Method to call (log, warn, error, info, debug)
   * @param {Array} args - Arguments to log
   */
  forwardToNative(method, args) {
    if (!this.isNative || !this.isAndroid) return;

    try {
      // Check if AndroidConsole interface is available
      if (typeof window.AndroidConsole === 'undefined') {
        return;
      }

      // Convert arguments to strings
      const message = args
        .map(arg => {
          if (typeof arg === 'object') {
            try {
              return JSON.stringify(arg, null, 2);
            } catch (e) {
              return String(arg);
            }
          }
          return String(arg);
        })
        .join(' ');

      // Forward to native Android Log using JavaScript interface
      window.AndroidConsole[method](message);
    } catch (error) {
      // Silently fail if native forwarding doesn't work
      // The original console will still log
    }
  }

  /**
   * Initialize console interception
   */
  init() {
    if (!this.isNative || !this.isAndroid) {
      this.originalConsole.log(
        'ConsoleLogger: Running on web or iOS, native Android logging disabled',
      );
      return;
    }

    this.originalConsole.log(
      'ConsoleLogger: Initializing native log forwarding for Android Logcat',
    );

    // Intercept console.log
    console.log = (...args) => {
      this.originalConsole.log(...args);
      this.forwardToNative('log', args);
    };

    // Intercept console.warn
    console.warn = (...args) => {
      this.originalConsole.warn(...args);
      this.forwardToNative('warn', args);
    };

    // Intercept console.error
    console.error = (...args) => {
      this.originalConsole.error(...args);
      this.forwardToNative('error', args);
    };

    // Intercept console.info
    console.info = (...args) => {
      this.originalConsole.info(...args);
      this.forwardToNative('info', args);
    };

    // Intercept console.debug
    console.debug = (...args) => {
      this.originalConsole.debug(...args);
      this.forwardToNative('debug', args);
    };

    this.originalConsole.log(
      '✓ ConsoleLogger: Initialized - Your logs will now appear in Android Logcat with tag "JSConsole"',
    );
  }

  /**
   * Restore original console methods
   */
  restore() {
    Object.assign(console, this.originalConsole);
    console.log('ConsoleLogger: Restored original console methods');
  }
}

// Export singleton instance
export const consoleLogger = new ConsoleLogger();

// Auto-initialize
export function initConsoleLogger() {
  consoleLogger.init();
}
